import { CSSProperties, ReactNode, useState } from "react";
import { LoadingOutlined } from "@ant-design/icons";
import { useModalAction } from "../../lib/hooks/useModalAction";

interface MIconButtonProps {
  icon: ReactNode;
  title?: string;
  onClick?: () => void | Promise<void>;
  disabled?: boolean;
  loading?: boolean;
  type?: "primary" | "danger";
  color?: string;
  background?: string;
  style?: CSSProperties;
}

const baseStyle: CSSProperties = {
  width: 44,
  height: 44,
  padding: 0,
  border: "none",
  boxSizing: "border-box",
  fontFamily: "inherit",
  borderRadius: 14,
  background: "#f3f4f6",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  fontSize: 20,
  color: "#6b7280",
  flexShrink: 0,
  WebkitTapHighlightColor: "transparent",
};

const typeStyles: Record<string, CSSProperties> = {
  primary: {
    background: "linear-gradient(135deg, #60a5fa, #3b82f6)",
    color: "white",
  },
  danger: {
    background: "linear-gradient(135deg, #ff6b6b, #ef4444)",
    color: "white",
  },
};

export function MIconButton({
  icon,
  title,
  onClick,
  disabled,
  loading: externalLoading,
  type,
  color,
  background,
  style,
}: MIconButtonProps) {
  const [internalLoading, setInternalLoading] = useState(false);
  const loading = externalLoading ?? internalLoading;
  const pending = internalLoading || !!externalLoading;
  const { isBusy: modalBusy, beginAction } = useModalAction(pending);
  const isDisabled = !!disabled || pending || modalBusy;

  async function handleClick() {
    if (!onClick || isDisabled) {
      return;
    }

    const result = onClick();

    if (result instanceof Promise) {
      beginAction();
      setInternalLoading(true);

      try {
        await result;
      } finally {
        setInternalLoading(false);
      }
    }
  }

  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      disabled={isDisabled}
      style={{
        ...baseStyle,
        ...(type ? typeStyles[type] : {}),
        ...(color ? { color } : {}),
        ...(background ? { background } : {}),
        ...(isDisabled && !loading ? { opacity: 0.4, cursor: "default" } : {}),
        ...style,
      }}
      onClick={handleClick}
    >
      {loading ? <LoadingOutlined /> : icon}
    </button>
  );
}
